import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../utils/config';

const PropertySearchBar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchSearchData();
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isDataLoading && searchQuery?.trim() && (properties.length > 0 || clients.length > 0)) {
      handleSearch(searchQuery);
    }
  }, [isDataLoading, properties, clients]);

  const fetchSearchData = async () => {
    setIsDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const [propertiesRes, clientsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROPERTY_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers }).catch(() => ({ data: [] }))
      ]);

      const propertiesData = Array.isArray(propertiesRes.data)
        ? propertiesRes.data
        : (propertiesRes.data?.results || propertiesRes.data?.data || propertiesRes.data?.properties || []);

      const clientsData = Array.isArray(clientsRes.data)
        ? clientsRes.data
        : (clientsRes.data?.results || clientsRes.data?.data || clientsRes.data?.clients || []);

      setProperties(propertiesData);
      setClients(clientsData);
    } catch (error) {
      setProperties([]);
      setClients([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  const mapPropertyToSearchResult = (property) => {
    const client = clients.find(c => c.id === property.client_id || c.client_id === property.client_id);
    const clientName = client?.name || client?.client_name || property?.bank_name || property?.customer_name || property?.client_name || '—';
    const address = property?.property_address || property?.address || 'Unknown address';
    const type = property?.property_type || property?.propertyType || '—';
    const statusMap = {
      completed: 'Completed',
      pending: 'Pending Review',
      in_progress: 'In Progress',
      scheduled: 'Scheduled'
    };
    const rawStatus = (property?.status || '').toString().toLowerCase().replace(/\s+/g, '_');
    const status = statusMap[rawStatus] || (property?.status || '—');
    const lastSurvey = property?.last_survey_date || property?.last_survey || null;

    return {
      id: property?.id,
      address,
      type,
      status,
      client: clientName,
      lastSurvey
    };
  };

  const handleSearch = (query) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!query?.trim()) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      const q = query.toLowerCase().trim();
      const filtered = properties
        .filter(property => {
          const addr = (property?.property_address || property?.address || '').toLowerCase();
          const customer = (property?.customer_name || '').toLowerCase();
          const bank = (property?.bank_name || '').toLowerCase();
          const contact = (property?.contact_person_name || '').toLowerCase();
          const type = (property?.property_type || property?.propertyType || '').toString().toLowerCase();
          const client = clients.find(c => c.id === property.client_id || c.client_id === property.client_id);
          const clientName = (client?.name || client?.client_name || '').toLowerCase();
          return addr.includes(q) || customer.includes(q) || bank.includes(q) ||
            contact.includes(q) || type.includes(q) || clientName.includes(q);
        })
        .slice(0, 10)
        .map(mapPropertyToSearchResult);

      setSearchResults(filtered);
      setShowResults(true);
      setIsSearching(false);
    }, 300);
  };

  const handleSelectProperty = (property) => {
    setShowResults(false);
    setSearchQuery(property?.address);
    navigate('/property-management');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
      case 'Surveyed':
        return 'text-success';
      case 'Pending Review':
      case 'In Progress':
        return 'text-warning';
      case 'Scheduled':
        return 'text-accent';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Input
            type="search"
            placeholder="Search properties, clients, or surveys..."
            value={searchQuery}
            onChange={(e) => handleSearch(e?.target?.value)}
            className="pl-10"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Icon 
              name={isSearching ? "Loader2" : "Search"} 
              size={16} 
              color="var(--color-muted-foreground)"
              className={isSearching ? "animate-spin" : ""}
            />
          </div>
        </div>
        
        <Button
          variant="outline"
          iconName="Filter"
          iconPosition="left"
          onClick={() => {/* Open advanced filters action */}}
        >
          Filters
        </Button>
      </div>
      {/* Search Results Dropdown */}
      {showResults && searchResults?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-elevation-3 z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-2 text-sm text-muted-foreground border-b border-border">
              <span>Found {searchResults?.length} properties</span>
              <button 
                onClick={() => setShowResults(false)}
                className="hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            
            {searchResults?.map((property) => (
              <button
                key={property?.id}
                onClick={() => handleSelectProperty(property)}
                className="w-full text-left p-3 hover:bg-muted rounded-md transition-smooth"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      {property?.address}
                    </h4>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className="flex items-center space-x-1">
                        <Icon name="Building" size={12} />
                        <span>{property?.type}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="User" size={12} />
                        <span>{property?.client}</span>
                      </span>
                      {property?.lastSurvey && (
                        <span className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} />
                          <span>{property?.lastSurvey}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs font-medium ${getStatusColor(property?.status)}`}>
                    {property?.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* No Results */}
      {showResults && searchResults?.length === 0 && searchQuery?.trim() && !isSearching && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-elevation-3 z-50">
          <div className="p-6 text-center">
            <Icon name="Search" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No properties found for "{searchQuery}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertySearchBar;